 <template>
  <div :style="computedStyle">
    <bubble-menu
      :editor="editor"
      :tippy-options="{ duration: 100 }"
      v-if="editor"
    >
      <div class="bubble-menu">
        <button @click="editor.chain().focus().toggleBold().run()" :class="{ 'is-active': editor.isActive('bold') }">
          <b>B</b>
        </button>
        <button @click="editor.chain().focus().toggleItalic().run()" :class="{ 'is-active': editor.isActive('italic') }">
          <i>I</i>
        </button>
        <button @click="editor.chain().focus().toggleStrike().run()" :class="{ 'is-active': editor.isActive('strike') }">
          <s>S</s>
        </button>
      </div>
    </bubble-menu>
    <editor-content :editor="editor" class="editor-container"/>
  </div>
</template>

<script>
import {BubbleMenu, EditorContent, Editor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import HardBreak from '@tiptap/extension-hard-break';
import Underline from '@tiptap/extension-underline';
import Text from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextStyle from '@tiptap/extension-text-style';
import Paragraph from '@tiptap/extension-paragraph'
import { Color } from '@tiptap/extension-color'
import { Extension } from '@tiptap/core'
import { Plugin } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { mergeAttributes } from '@tiptap/core'
import eventBus from '@/eventBus.js'

const CustomEnter = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.setHardBreak(),
    }
  },
})

export default {
  name: 'BlockEditor',
  components: { EditorContent, BubbleMenu },
  props: {
    data: {
      type: Object,
      required: true
    },
    styleObject: {
      type: Object,
      default: () => ({})
    },
    // Eindeutige Kennung, um den richtigen Editor zu identifizieren
    contentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      editor: null
    }
  },
  computed: {
    computedStyle() {
      return this.styleObject
    }
  },
mounted() {
  const fontSize = 16; // Basis-Schriftgröße – ggf. dynamisch anpassen
  const placeholderPlugin = new Plugin({
  props: {
    decorations(state) {
      const decorations = [];

      // beide Muster in einer Liste, mit jeweiligem Icon und Farbe
      const patterns = [
        {
          regex: /\[\[(.*?)\]\]/g,
          icon: 'bolt',
          color: '#05bbef',
        },
        {
          regex: /%%(.*?)%%/g,
          icon: 'rule',
          color: 'red',
        },
      ];

      state.doc.descendants((node, pos) => {
        if (!node.isText) return;
        const text = node.text;

        for (const { regex, icon, color } of patterns) {
          let match;
          // Wichtig: regex.lastIndex zurücksetzen, falls mehrfach dieselbe Instanz genutzt wird
          regex.lastIndex = 0;
          while ((match = regex.exec(text)) !== null) {
            const fullStart = pos + match.index;
            const fullEnd = fullStart + match[0].length;
            const openStart = fullStart;
            const openEnd = openStart + 2;            // "[[" oder "%%" hat 2 Zeichen
            const closeStart = fullEnd - 2;          // "]]" oder "%%" am Ende
            const closeEnd = fullEnd;

            // 1) Klammern/Prozentzeichen ausblenden
            decorations.push(
              Decoration.inline(openStart, openEnd, { style: 'display: none;' })
            );
            decorations.push(
              Decoration.inline(closeStart, closeEnd, { style: 'display: none;' })
            );

            // 2) Widget vor Textanfang (open)
            decorations.push(
              Decoration.widget(openStart, () => {
                const span = document.createElement('span');
                span.className = 'material-symbols-outlined';
                span.style.fontSize = `${fontSize - 5}px`;
                span.style.color = color;
                span.innerText = icon;
                return span;
              }, { side: -1 })
            );

            // 3) Widget nach Textende (close)
            decorations.push(
              Decoration.widget(closeEnd, () => {
                const span = document.createElement('span');
                span.className = 'material-symbols-outlined';
                span.style.fontSize = `${fontSize - 5}px`;
                span.style.color = color;
                span.innerText = icon;
                return span;
              }, { side: 1 })
            );
          }
        }
      });

      return DecorationSet.create(state.doc, decorations);
    },
  },
});
  this.editor = new Editor({
    extensions: [
      CustomEnter,
      Underline,
      Superscript,
      Subscript,
      TextStyle,
      Color,
      Link.extend({
            addAttributes() {
              return {
                ...this.parent?.(),
                target: {
                  default: '_blank',
                  parseHTML: element => element.getAttribute('target') || '_blank',
                },
                style: {
                default: 'text-decoration: none; cursor: pointer',
                parseHTML: element => element.getAttribute('style') || '',
              },
              };
            },
          }),
        StarterKit.configure({ hardBreak: true }),
        Paragraph.extend({
          parseHTML() {
            // Ersetzt alle <p> beim Parsen durch <div>
            return [{ tag: 'div' }]
          },
          renderHTML({ HTMLAttributes }) {
            // Rendert jeden Absatz als <div> ohne default-Margin
            return [
              'div',
              mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
              0,
            ]
          },
        }),
        HardBreak.configure({ keepMarks: true, keepAttributes: true }),
        Text.configure({ HTMLAttributes: { preserveWhitespace: true } })
    ],
    // Hier wird der Originalinhalt (mit [[ und ]] etc.) geladen.
    content: this.data.props.text,
    editorProps: {
      // Füge unser Decoration-Plugin zu den Editor-Plugins hinzu.
      plugins: [placeholderPlugin]
    },
    onUpdate: ({ editor }) => {
      // Aktualisiere den Content – der zugrunde liegende Text bleibt erhalten.
      this.data.props.text = editor.getHTML();
    },
    onSelectionUpdate: ({ editor }) => {
      const selectionAttributes = editor.getAttributes('textStyle');
      eventBus.emit('updateTextStyle', { color: selectionAttributes.color });
    },
  });


    // Abonniere die Events und prüfe jeweils, ob die contentId übereinstimmt
    eventBus.on('toggleBold', this.handleToggleBold)
    eventBus.on('toggleItalic', this.handleToggleItalic)
    eventBus.on('toggleUnderline', this.handleToggleUnderline)
    eventBus.on('toggleStrike', this.handleToggleStrike)
    eventBus.on('markPlaceholder', this.handleMarkPlaceholder)
    eventBus.on('enterRule', this.handleEnterRule)
    eventBus.on('addLink', this.handleAddLink)
    eventBus.on('undo', this.handleUndo)
    eventBus.on('redo', this.handleRedo)
    eventBus.on('subscript', this.handleSubscript)
    eventBus.on('superscript', this.handleSuperscript)
    eventBus.on('toggleColor', this.handleColor)
    eventBus.on('resetColor', this.handleColor)
    eventBus.on('uploadDocx', this.handleDocx)
  },
  methods: {
    handleToggleBold(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().toggleBold().run()
      }
    },
    handleToggleItalic(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().toggleItalic().run()
      }
    },
    handleToggleUnderline(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().toggleUnderline().run()
      }
    },
    handleToggleStrike(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().toggleStrike().run()
      }
    },
    handleMarkPlaceholder(payload) {
      if (payload.contentId === this.contentId) {
        const { state, dispatch } = this.editor.view
        const { from, to } = this.editor.state.selection
        if (from === to) return
        const selectedText = state.doc.textBetween(from, to)
        const placeholderText = `[[${selectedText}]]`
        dispatch(state.tr.insertText(placeholderText, from, to))
      }
    },
    handleEnterRule(payload) {
      if (payload.contentId === this.contentId) {
        const { state, dispatch } = this.editor.view
        const { from, to } = this.editor.state.selection
        if (from === to) return
        const selectedText = state.doc.textBetween(from, to)
        const placeholderText = `%%${selectedText}%%`
        dispatch(state.tr.insertText(placeholderText, from, to))
      }
    },
    handleAddLink(payload) {
      if (payload.contentId === this.contentId) {
        const currentLink = this.editor.getAttributes('link').href || ''
        const url = prompt('Enter the URL', currentLink)
        if (url) {
          this.editor.chain().focus().setLink({ href: url, target: '_blank' }).run()
        }
      }
    },
    handleUndo(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().undo().run()
      }
    },
    handleRedo(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().redo().run()
      }
    },
    handleSuperscript(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().setSuperscript().run()
      }
    },
    handleSubscript(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().setSubscript().run()
      }
    },
    handleColor(payload) {
      if (payload.contentId === this.contentId) {
        console.log(payload.colorCode)
        this.editor.chain().focus().setColor(payload.colorCode).run()
      }
    },
    resetColor(payload) {
      if (payload.contentId === this.contentId) {
        this.editor.chain().focus().unsetColor().run()
      }
    },
    handleDocx(payload){
      if (payload.contentId === this.contentId && this.editor) {
        // Setze den Editor-Inhalt auf das konvertierte HTML
        this.editor.commands.setContent(payload.html);
      }
    }

  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
    }
    // Entferne alle Event-Listener
    eventBus.off('toggleBold', this.handleToggleBold)
    eventBus.off('toggleItalic', this.handleToggleItalic)
    eventBus.off('toggleUnderline', this.handleToggleUnderline)
    eventBus.off('toggleStrike', this.handleToggleStrike)
    eventBus.off('markPlaceholder', this.handleMarkPlaceholder)
    eventBus.off('enterRule', this.handleEnterRule)
    eventBus.off('addLink', this.handleAddLink)
    eventBus.off('undo', this.handleUndo)
    eventBus.off('redo', this.handleRedo)
    eventBus.off('subscript', this.handleSubscript)
    eventBus.off('superscript', this.handleSuperscript)
    eventBus.off('toggleColor', this.handleColor)
    eventBus.off('resetColor', this.handleColor)
    eventBus.off('uploadDocx', this.handleDocx)
  }
}
</script>

<style scoped>
.editor-container .ProseMirror {
  margin: 0;
  padding: 0;
}
.editor-container .ProseMirror p {
  margin: 0;
  padding: 0;
}



/* .editor-container {
  margin-top: 1em !important; 
  margin-bottom: 0; 
}  */

/* Bubble menu */
.bubble-menu {
  background-color: white;
  border: 1px solid   #cccc;
  border-radius: 0.7rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  padding: 0.2rem;
  gap: 5px;
}

button{
  background-color: transparent;
  border: none;
  border-radius: 100%;
  font-size: 18px;
}

button:hover {
  background: rgb(241, 238, 238);
}

</style>
  